import { ApiError, apiFetch } from "@/lib/api/client";
import { notifyBackgroundError } from "@/lib/dialogs/globalErrorBridge";
import { reportClientError } from "@/lib/monitoring";
import type { ClientActivityEntry, CredentialsEmailResponse, InvitationResponse, ReactivateResponse, UpdateClientResponse } from "@/lib/types/api";
import type { BootstrapResponse } from "@/lib/types/session";
import type { Client, LicenseConfig } from "@/lib/types/client";

/** Liefert eine Kopie mit aktualisiertem lastActive, wie es das alte saveClient() optimistisch tat. */
export function touchClient(client: Client): Client {
  return { ...client, lastActive: new Date().toISOString() };
}

interface SaveEntry {
  client: Client;
  timer: ReturnType<typeof setTimeout> | null;
  inFlight: boolean;
  dirty: boolean;
  resolvers: Array<{ resolve: (value: UpdateClientResponse) => void; reject: (reason: unknown) => void }>;
}

const saveEntries = new Map<string, SaveEntry>();

function scheduleFlush(clientId: string, delay = 350) {
  const entry = saveEntries.get(clientId);
  if (!entry) return;
  if (entry.timer) clearTimeout(entry.timer);
  entry.timer = setTimeout(() => flushSave(clientId), delay);
}

async function flushSave(clientId: string) {
  const entry = saveEntries.get(clientId);
  if (!entry || entry.inFlight) return;
  entry.timer = null;
  entry.inFlight = true;
  entry.dirty = false;
  const resolvers = entry.resolvers;
  entry.resolvers = [];

  try {
    const result = await apiFetch<UpdateClientResponse>(`/clients/${encodeURIComponent(clientId)}`, {
      method: "PUT",
      body: JSON.stringify(entry.client),
      keepalive: true
    });
    entry.inFlight = false;
    // Der Server erhöht "version" bei jedem erfolgreichen Save (optimistisches Lock,
    // server/routes/clients.js). Ohne dieses Zurückschreiben würde der nächste Save
    // (egal ob sofort dank entry.dirty oder erst beim nächsten mutate()-Aufruf) mit
    // der veralteten Version scheitern (409) — genau das tat saveState.client.version
    // im ursprünglichen src/db.js ebenfalls.
    entry.client = { ...entry.client, version: result.version, lastActive: result.savedAt };
    resolvers.forEach((resolver) => resolver.resolve(result));
    if (entry.dirty) scheduleFlush(clientId, 0);
    else saveEntries.delete(clientId);
  } catch (error) {
    entry.inFlight = false;
    saveEntries.delete(clientId);
    resolvers.forEach((resolver) => resolver.reject(error));
    reportClientError(error, { source: "client-autosave" });
    notifyBackgroundError(error instanceof ApiError ? error.message : "Die Anfrage ist fehlgeschlagen.");
  }
}

/**
 * Debouncte, koaleszierende Autosave-Queue: 1:1-Verhalten von scheduleClientSave/
 * flushClientSave/saveClient aus src/db.js (350ms pro Client, keepalive-PUT,
 * erneuter Flush wenn während der Anfrage ein neuerer Stand ankam). Anders als
 * im Original mutiert dies NICHT das übergebene Client-Objekt — Aufrufer bauen
 * den nächsten Stand selbst (z. B. via touchClient) und aktualisieren ihren
 * eigenen React-State separat.
 */
export function saveClient(client: Client, delay = 350): Promise<UpdateClientResponse> {
  return new Promise((resolve, reject) => {
    let entry = saveEntries.get(client.id);
    if (!entry) {
      entry = { client, timer: null, inFlight: false, dirty: false, resolvers: [] };
      saveEntries.set(client.id, entry);
    } else {
      entry.client = client;
      entry.dirty = true;
    }
    entry.resolvers.push({ resolve, reject });
    scheduleFlush(client.id, delay);
  });
}

export function flushPendingSaves(): Promise<PromiseSettledResult<UpdateClientResponse>[]> {
  const pending: Promise<UpdateClientResponse>[] = [];
  for (const [clientId, entry] of saveEntries) {
    if (entry.timer) scheduleFlush(clientId, 0);
    pending.push(new Promise((resolve, reject) => entry.resolvers.push({ resolve, reject })));
  }
  return Promise.allSettled(pending);
}

export async function createClient(
  name: string,
  email: string,
  primaryAdvisorId = "",
  backupAdvisorId = "",
  firstName = "",
  lastName = "",
  licenses?: LicenseConfig
): Promise<{ client: Client; invitation: InvitationResponse }> {
  return apiFetch<{ client: Client; invitation: InvitationResponse }>("/clients", {
    method: "POST",
    body: JSON.stringify({ name, email, primaryAdvisorId, backupAdvisorId, firstName, lastName, licenses })
  });
}

/**
 * Was am Kunden hängt (S5.8) — die Zahlen für die Rückfrage vor dem Löschen,
 * und die Auskunft, ob der erste der zwei Schritte schon getan ist.
 */
export interface PurgePreview {
  id: string;
  name: string;
  status: string;
  /** Gelöscht wird nur aus dem Archiv. */
  archived: boolean;
  tenant: { id: string; name: string; slug: string } | null;
  counts: { members: number; orders: number; attachments: number; signatures: number; reports: number; files: number };
}

export function purgePreview(clientId: string): Promise<PurgePreview> {
  return apiFetch<PurgePreview>(`/clients/${encodeURIComponent(clientId)}/purge-preview`);
}

/**
 * Löscht Kunde **und** Mandant, unwiderruflich. Verlangt den wörtlich
 * getippten Kundennamen — dieselbe Vorsichtsmaßnahme, die GitHub beim Löschen
 * eines Repositorys trifft, und aus demselben Grund: ein „Wirklich?"-Dialog
 * wird weggeklickt, ein Name wird abgetippt.
 */
export function deleteClient(clientId: string, confirmName: string): Promise<{ ok: true }> {
  return apiFetch<{ ok: true }>(`/clients/${encodeURIComponent(clientId)}`, { method: "DELETE", body: JSON.stringify({ confirmName }) });
}

export function createInvitation(clientId: string): Promise<InvitationResponse> {
  return apiFetch<InvitationResponse>(`/clients/${encodeURIComponent(clientId)}/invitation`, { method: "POST" });
}

export function reactivateClient(clientId: string): Promise<ReactivateResponse> {
  return apiFetch<ReactivateResponse>(`/clients/${encodeURIComponent(clientId)}/reactivate`, { method: "POST" });
}

export function startStaffWork(clientId: string): Promise<{ staffStartedAt: string }> {
  return apiFetch<{ staffStartedAt: string }>(`/clients/${encodeURIComponent(clientId)}/staff-start`, { method: "POST" });
}

export function impersonateClient(clientId: string): Promise<BootstrapResponse> {
  return apiFetch<BootstrapResponse>(`/clients/${encodeURIComponent(clientId)}/impersonate`, { method: "POST" });
}

export function sendCredentialsEmail(clientId: string, scheduledAt: string | null): Promise<CredentialsEmailResponse> {
  return apiFetch<CredentialsEmailResponse>(`/clients/${encodeURIComponent(clientId)}/credentials-email`, {
    method: "POST",
    body: JSON.stringify({ scheduledAt })
  });
}

export function cancelCredentialsEmail(clientId: string): Promise<{ ok: true }> {
  return apiFetch<{ ok: true }>(`/clients/${encodeURIComponent(clientId)}/credentials-email`, { method: "DELETE" });
}

export function getClientActivity(clientId: string): Promise<ClientActivityEntry[]> {
  return apiFetch<ClientActivityEntry[]>(`/clients/${encodeURIComponent(clientId)}/activity`);
}

export function exportClientsCSV(clients: Client[]): string {
  let csv =
    "﻿Kunde ID;Name;Status;Aktiviert;Letzte Aktivität;Kontaktperson;E-Mail;Telefon;Benutzer Anzahl;Fahrzeuge Anzahl\n";
  for (const client of clients) {
    const master = client.data?.firmendaten;
    const values = [
      client.id,
      client.name,
      client.status,
      client.activated ? "JA" : "NEIN",
      client.lastActive,
      master ? `${master.contactFirstName} ${master.contactLastName}`.trim() : "",
      master?.email,
      master?.phone,
      client.data?.usersTeams?.users?.length || 0,
      client.data?.vehicles?.length || 0
    ];
    csv += `${values.map((value) => String(value ?? "").replaceAll(";", ",").replaceAll("\n", " ")).join(";")}\n`;
  }
  return csv;
}
